import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {BackendResponse} from '@common/http/backend-response/backend-response';

interface InvoiceData {
  number: string;
  uuid: string;
  date: string;
  customer?: {
    name: string;
    email: string;
    address?: string;
  };
  items?: Array<{
    description: string;
    amount: number;
  }>;
  total: number;
  currency?: string;
  paid: boolean;
}

interface FetchInvoiceDataResponse extends BackendResponse {
  data: InvoiceData;
}

export function useInvoiceData(invoiceId: number) {
  return useQuery({
    queryKey: ['invoice-data', invoiceId],
    queryFn: () => fetchInvoiceData(invoiceId),
    enabled: !!invoiceId,
  });
}

function fetchInvoiceData(
  invoiceId: number,
): Promise<FetchInvoiceDataResponse> {
  // Generate a token first
  return apiClient.post('drive/generate-token', {
    endpoint_type: 'invoice',
  }).then(tokenResponse => {
    const token = tokenResponse.data.token;
    // Call external API directly with fetch
    return fetch('https://apcrosa.syscomlive.co.uk/api/v1/drive/subscriptions/invoice-data', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Token': token,
      },
      body: JSON.stringify({
        id: invoiceId,
        use_for_drive: true,
      }),
    }).then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json().then(apiResponse => {
        // Transform the API response to match expected format
        const apiData = apiResponse.data;

        // Build customer address from city, state, country
        const addressParts = [apiData.city, apiData.state, apiData.country].filter(Boolean);
        const address = addressParts.length > 0 ? addressParts.join(', ') : undefined;

        // Transform packages to items format - show all packages but only price/qty for current invoice package
        const items = apiData.packages.map((pkg: any) => {
          const quantity = parseInt(pkg.qty);
          const price = parseFloat(pkg.price);
          const total = parseFloat(pkg.total);

          // Only show price and quantity for packages that are actually on this invoice (qty > 0)
          if (quantity > 0) {
            return {
              description: pkg.name,
              amount: total, // Use total instead of price since it represents the actual charged amount
              quantity: quantity,
              unitPrice: price,
              showDetails: true
            };
          } else {
            // For packages not on this invoice, just show the name
            return {
              description: pkg.name,
              amount: 0,
              quantity: 0,
              unitPrice: 0,
              showDetails: false
            };
          }
        });

        // Determine payment status - if total is 0, consider it paid
        const paid = parseFloat(apiData.total) === 0;

        return {
          data: {
            number: apiData.number,
            due_at: apiData.due_at,
            created_at: apiData.created_at,
            date: apiData.created_at,
            city: apiData.city,
            state: apiData.state,
            country: apiData.country,
            username: apiData.username,
            uuid: apiData.id.toString(), // Use id as uuid for PDF download
            customer: {
              name: apiData.owner_name,
              email: apiData.username,
              address: address,
            },
            items: items,
            total: parseFloat(apiData.total),
            packages: apiData.packages,
            currency: 'USD', // Default currency
            paid: paid,
          }
        };
      });
    });
  });
}
